import React, {useEffect, useState} from "react";
import {DataGrid, GridActionsCellItem} from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Paper,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  AppBar,
  Card,
  Chip
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
  Visibility,
  AlarmAdd as ExtendIcon
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {GET} from "APIclient";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import properties from "constants/ApplicationConstants";
import {useNavigate} from "react-router-dom";
import dayjs from "dayjs";
// Add to imports at top:
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";

// Reusing your exact CustomPagination component
function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading
}) {
  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        p: 1,
        borderTop: "1px solid #e0e0e0",
        gap: 2,
        flexWrap: "nowrap"
      }}
    >
      <Typography variant="caption" color="text.secondary" noWrap>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No records"}
      </Typography>

      <Box sx={{display: "flex", alignItems: "center", gap: 1, flexShrink: 0}}>
        <Typography variant="caption" sx={{mr: 2, color: "#666"}}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton onClick={() => onPageChange(0)} disabled={page === 0 || loading} size="small">
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous">
          <IconButton
            onClick={() => onPageChange(Math.max(0, page - 1))}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next">
          <IconButton
            onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={onGoToLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {backgroundColor: "#1565c0"},
              "&.Mui-disabled": {backgroundColor: "#e0e0e0", color: "#9e9e9e"}
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function AppraisalStatusReport() {
  const navigate = useNavigate();

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [pageSize] = React.useState(20);
  const [totalRows, setTotalRows] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);

  const [empId, setEmpId] = React.useState("");
  const [empName, setEmpName] = useState("");
  const [empAppraisal, setEmpAppraisal] = React.useState("");
  const [managerAppraisal, setManagerAppraisal] = React.useState("");

  const [snackbar, setSnackbar] = React.useState({open: false, message: "", severity: "success"});

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({open: true, message, severity});
  };

  const handleCloseSnackbar = () => setSnackbar(prev => ({...prev, open: false}));

  const fetchData = async (page = 0) => {
    setLoading(true);
    try {
      let url = `admin/appraisal/status-report?page=${page}&size=${pageSize}`;

      if (empId !== null && empId !== undefined && empId !== "") {
        url += `&empId=${encodeURIComponent(empId.trim())}`;
      }
      if (empName !== null && empName !== undefined && empName !== "") {
        url += `&empName=${empName}`;
      }
      if (empAppraisal !== null && empAppraisal !== undefined && empAppraisal !== "") {
        url += `&empAppraisal=${empAppraisal}`;
      }
      if (managerAppraisal !== null && managerAppraisal !== undefined && managerAppraisal !== "") {
        url += `&managerAppraisal=${managerAppraisal}`;
      }
      const response = await GET(url);

      if (response?.status === 200) {
        const data = response?.data?.data?.paginatedData || [];

        const formatted = data.map((item, index) => {
          let skillsArray = [];
          try {
            skillsArray = JSON.parse(item.applicableSkills || "[]");
          } catch (e) {
            skillsArray = [];
          }

          return {
            id: page * pageSize + index + 1,
            emp_id: item.empId,
            appraisalId: item.appraisalId,
            employee_name: item.employeeName || "-",
            manager_name: item.reportingManagerName || "-",
            applicable_skills: skillsArray.length > 0,
            emp_permission: item.empPermission,
            manager_permission: item.managerPermission,
            status: true
          };
        });

        setRows(formatted);
        setTotalRows(response.data.data.totalItems || 0);
        setTotalPages(response.data.data.totalPages || 0);
      }
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = () => {
    setPage(0);
    fetchData(0);
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    fetchData(newPage);
  };

  const handleGoToLastPage = () => {
    if (totalPages > 0) handlePageChange(totalPages - 1);
  };

  const StatusChip = ({label, color}) => (
    <Chip
      label={label}
      color={color}
      size="small"
      sx={{
        fontWeight: "500",
        minWidth: "90px",
        color: "#fff",
        fontFamily: properties.fontFamily
      }}
    />
  );

  const columns = [
    {field: "id", headerName: "#", width: 30, align: "center", headerAlign: "center"},
    {field: "emp_id", headerName: "EMP ID", width: 70},
    {field: "employee_name", headerName: "Employee Name", width: 170},
    {field: "manager_name", headerName: "Manager Name", width: 170},
    {
      field: "applicable_skills",
      headerName: "KRA Assign",
      width: 140,
      align: "center",
      headerAlign: "center",
      renderCell: params =>
        params.value ? (
          <StatusChip label="Completed" color="success" />
        ) : (
          <StatusChip label="Pending" color="error" />
        )
    },
    {
      field: "emp_permission",
      headerName: "Employee Rating",
      width: 180,
      align: "center",
      headerAlign: "center",
      renderCell: params => {
        const hasKRA = params.row.applicable_skills;
        const empPerm = params.row.emp_permission;

        // 1. KRA not assigned yet → Not Assigned (Yellow)
        if (!hasKRA) {
          return <StatusChip label="Not Assigned" color="warning" />;
        }

        // 2. KRA assigned + employee still has permission (not submitted)
        if (empPerm === 1) {
          return <StatusChip label="Pending" color="error" />;
        }

        // 3. KRA assigned + employee submitted (permission locked)
        return <StatusChip label="Completed" color="success" />;
      }
    },
    {
      field: "manager_permission",
      headerName: "Manager Rating",
      width: 180,
      align: "center",
      headerAlign: "center",
      renderCell: params => {
        const hasKRA = params.row.applicable_skills;
        const mgrPerm = params.row.manager_permission;

        if (!hasKRA) {
          return <StatusChip label="Not Assigned" color="warning" />;
        }

        if (mgrPerm === 1) {
          return <StatusChip label="Pending" color="error" />;
        }

        return <StatusChip label="Completed" color="success" />;
      }
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Action",
      width: 120,
      align: "center",
      getActions: params => {
        const actions = [];

        if (params.row.status) {
          actions.push(
            <GridActionsCellItem
              icon={
                <Tooltip title="View KRA Form">
                  <Visibility color="primary" />
                </Tooltip>
              }
              label="KRA Form"
              onClick={() => {
                const empId = params.row.emp_id;
                const empName = `${params.row.employee_name}`.trim();
                const appraisalId = params.row.appraisalId;
                const encodedName = encodeURIComponent(empName);
                console.log("empId:", empId);
                console.log("appraisalId:", appraisalId);

                navigate(`/admin/appraisal/admin_kra_form_overview/${appraisalId}/${empId}`);
              }}
            />
          );
        }

        const canExtend =
          !params.row.applicable_skills ||
          params.row.emp_permission === 1 ||
          params.row.manager_permission === 1;

        if (canExtend) {
          actions.push(
            <GridActionsCellItem
              icon={
                <Tooltip title="Extend KRA">
                  <ExtendIcon color="action" />
                </Tooltip>
              }
              label="Extend KRA"
              onClick={() => {
                const empId = params.row.emp_id;
                const empName = `${params.row.employee_name}`.trim();
                const encodedName = encodeURIComponent(empName);

                navigate(`/extend-kra-employee?empId=${empId}&name=${empName}`);
              }}
            />
          );
        }

        return actions;
      }
    }
  ];

  const [evalStartDate, setEvalStartDate] = useState(null);
  const [evalEndDate, setEvalEndDate] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    const fetchCurrentKraDates = async () => {
      setIsLoadingData(true);
      try {
        const url = "admin/appraisal-cycle/last";
        const response = await GET(url);
        if (response?.status === 200) {
          const data = response?.data?.data;
          setEvalStartDate(data?.evalutionStartDate ? dayjs(data?.evalutionStartDate) : null);
          setEvalEndDate(data?.evalutionEndDate ? dayjs(data.evalutionEndDate) : null);
        }
      } catch (error) {
        showSnackbar(error?.response?.data?.message || "Failed to load current KRA dates", "error");
      } finally {
        setIsLoadingData(false);
      }
    };
    fetchCurrentKraDates();
  }, []);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      let url = `admin/appraisal/status-report?page=0&size=10000`;
      if (empId?.trim()) url += `&empId=${encodeURIComponent(empId.trim())}`;
      if (empAppraisal) url += `&empAppraisal=${empAppraisal}`;
      if (managerAppraisal) url += `&managerAppraisal=${managerAppraisal}`;

      const response = await GET(url);
      if (response?.status !== 200) throw new Error("Failed to fetch data");

      const data = response?.data?.data?.paginatedData || [];

      const resolveStatus = (hasKRA, permission) => {
        if (!hasKRA) return "Not Assigned";
        return permission === 1 ? "Pending" : "Completed";
      };

      const exportRows = data.map((item, index) => {
        let skillsArray = [];
        try {
          skillsArray = JSON.parse(item.applicableSkills || "[]");
        } catch {
          skillsArray = [];
        }
        const hasKRA = skillsArray.length > 0;

        return {
          "#": index + 1,
          "EMP ID": item.empId || "-",
          "Employee Name": item.employeeName || "-",
          "Manager Name": item.reportingManagerName || "-",
          "KRA Assign": hasKRA ? "Completed" : "Pending",
          "Employee Rating": resolveStatus(hasKRA, item.empPermission),
          "Manager Rating": resolveStatus(hasKRA, item.managerPermission)
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportRows);

      const colWidths = Object.keys(exportRows[0] || {}).map(key => ({
        wch: Math.max(key.length, ...exportRows.map(r => String(r[key] ?? "").length)) + 2
      }));
      worksheet["!cols"] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Appraisal Status Report");

      const fileName = `Appraisal_Status_Report_${dayjs().format("YYYY-MM-DD")}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      showSnackbar("Excel downloaded successfully!", "success");
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Failed to download Excel", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static" color="transparent" elevation={0}>
          <Card>
            <MDBox pt={2} pb={2} px={3}>
              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(12, 1fr)",
                  gap: 2,
                  alignItems: "center"
                }}
              >
                <Typography
                  sx={{
                    gridColumn: {xs: "span 12", sm: "span 2"},
                    color: "text.primary",
                    fontFamily: properties.fontFamily,
                    fontSize: "17px",
                    fontWeight: "bold"
                  }}
                >
                  Status Report
                </Typography>

                <MDInput
                  label="Employee ID"
                  value={empId}
                  onChange={e => setEmpId(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                  sx={{gridColumn: {xs: "span 12", sm: "span 2"}}}
                />

                <MDInput
                  label="Employee Name"
                  value={empName}
                  onChange={e => setEmpName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                  sx={{gridColumn: {xs: "span 12", sm: "span 2"}}}
                />

                {/* Employee Rating */}
                <FormControl variant="outlined" sx={{m: 0, width: "15ch", gridColumn: "span 2"}}>
                  <InputLabel>Self Rating</InputLabel>
                  <Select
                    value={empAppraisal}
                    label="Employee Rating"
                    onChange={e => setEmpAppraisal(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSearch()}
                    sx={{minWidth: 150, height: 43}}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="3">Completed</MenuItem>
                    <MenuItem value="1">Pending</MenuItem>
                    <MenuItem value="2">Not Assigned</MenuItem>
                  </Select>
                </FormControl>

                {/* Manager Rating */}
                <FormControl variant="outlined" sx={{m: 0, width: "15ch", gridColumn: "span 2"}}>
                  <InputLabel>Manager Rating</InputLabel>
                  <Select
                    value={managerAppraisal}
                    label="Manager Rating"
                    onChange={e => setManagerAppraisal(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSearch()}
                    sx={{minWidth: 150, height: 43}}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="3">Completed</MenuItem>
                    <MenuItem value="1">Pending</MenuItem>
                  </Select>
                </FormControl>

                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleSearch}
                  sx={{
                    gridColumn: {xs: "span 12", sm: "span 2"},
                    background: properties.colors.bgcolorbluethem || "#1976d2",
                    height: "40px",
                    "&:hover": {
                      background: properties.colors.bgcolorbluethem
                        ? `${properties.colors.bgcolorbluethem}ee`
                        : "#1565c0"
                    }
                  }}
                >
                  Search
                </MDButton>
              </MDBox>
              <MDBox
                sx={{
                  mt: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}
              >
                {(evalStartDate || evalEndDate) && (
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "text.secondary",
                      fontFamily: properties.fontFamily
                    }}
                  >
                    <span style={{fontWeight: "bold", color: "#1976d2"}}>Note:</span> Status Report
                    for current appraisal cycle:{" "}
                    <span style={{fontWeight: "600", color: "#333"}}>
                      {evalStartDate ? evalStartDate.format("DD MMM YYYY") : "N/A"}
                    </span>{" "}
                    to{" "}
                    <span style={{fontWeight: "600", color: "#333"}}>
                      {evalEndDate ? evalEndDate.format("DD MMM YYYY") : "N/A"}
                    </span>
                  </Typography>
                )}

                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={handleDownloadExcel}
                  sx={{
                    ml: "auto",
                    height: "40px",
                    minWidth: "150px",
                    fontSize: "12px",
                    px: 3
                  }}
                >
                  Download
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>
      </MDBox>

      <MDBox>
        <Paper>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={totalRows}
            pageSizeOptions={[pageSize]}
            disableRowSelectionOnClick
            sx={{
              fontFamily: properties.fontFamily,
              fontSize: "14px",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                fontWeight: "bold"
              },
              height: "490px",
              "& .MuiDataGrid-scrollbar": {
                width: "10px !important"
              }
            }}
            slots={{
              pagination: () => (
                <CustomPagination
                  page={page}
                  pageSize={pageSize}
                  totalRows={totalRows}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onGoToLastPage={handleGoToLastPage}
                  loading={loading}
                />
              )
            }}
          />
        </Paper>
      </MDBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{vertical: "top", horizontal: "right"}}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{width: "100%"}}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
